import {
  type InvestorDealViewModel,
  type DealGroupByAssetClass,
  type DealGroupBySubAssetClass,
  type DealGroupByRole,
  type InvestorViewModel,
  type Tranche,
  type InvestorInfo,
  type InvestorPropertiesViewModel,
  type InvestorRatings,
  type RatingDeals,
  type InvestorDetailViewModel,
} from '@/types/investor';
import BaseService from './BaseService';

export class InvestorService extends BaseService {
  private baseURL = '/api/deals';

  getAllInvestors = (): Promise<InvestorViewModel[]> => {
    return this.get<InvestorViewModel[]>({ baseUrl: this.baseURL, path: 'investors' });
  };

  getInvestorDetailsByName = (investorName: string): Promise<InvestorDetailViewModel> => {
    return this.get<InvestorDetailViewModel>({ baseUrl: this.baseURL, path: `investors/${investorName}/Details` });
  };

  getInvestorInfoByInvestorName(investorName: string): Promise<InvestorInfo> {
    return this.get<InvestorInfo>({
      baseUrl: this.baseURL,
      path: `investors/${investorName}/info`,
    });
  }

  GetDealsByInvestorId = (investorId: string): Promise<InvestorDealViewModel[]> => {
    return this.get<InvestorDealViewModel[]>({ baseUrl: this.baseURL, path: `investors/${investorId}/deals` });
  };

  GetDealsAssetClassByInvestorId = (investorId: string, period?: string): Promise<DealGroupByAssetClass[]> => {
    return this.get<DealGroupByAssetClass[]>({
      baseUrl: this.baseURL,
      path: `investors/${investorId}/dealsByAssetClass`,
      params: { period },
    });
  };

  GetDealsSubAssetClassByInvestorId = (
    investorId: string,
    assetClassStr?: string,
    period?: string,
  ): Promise<DealGroupBySubAssetClass[]> => {
    return this.get<DealGroupBySubAssetClass[]>({
      baseUrl: this.baseURL,
      path: `investors/${investorId}/dealsBySubAssetClass`,
      params: { assetClassStr, period },
    });
  };

  GetInvestorRoleSummaryByInvestorIdAndPeriod = (investorId: string, period?: string): Promise<DealGroupByRole[]> => {
    return this.get<DealGroupByRole[]>({
      baseUrl: this.baseURL,
      path: `investors/${investorId}/dealsByRoles`,
      params: { period },
    });
  };

  GetTranchesByInvestorIdAndDeal(investorId: string, dealId: string): Promise<Tranche[]> {
    return this.get<Tranche[]>({
      baseUrl: this.baseURL,
      path: `investors/${investorId}/${dealId}`,
    });
  }

  GetInvestorPropertiesByInvestorIdAndPeriod(
    investorId: string,
    period?: string,
  ): Promise<InvestorPropertiesViewModel> {
    return this.get<InvestorPropertiesViewModel>({
      baseUrl: this.baseURL,
      path: `investors/${investorId}/properties`,
      params: { period },
    });
  }

  GetRatingSummaryByInvestorId(investorId: string, period?: string): Promise<InvestorRatings[]> {
    return this.get<InvestorRatings[]>({
      baseUrl: this.baseURL,
      path: `investors/${investorId}/rating-summary`,
      params: { period },
    });
  }

  GetDealsRatingByInvestorId(investorId: string, rating: string, period?: string): Promise<RatingDeals[]> {
    return this.get<RatingDeals[]>({
      baseUrl: this.baseURL,
      path: `investors/${investorId}/ratings/${rating}`,
      params: { period },
    });
  }

  // Alias for compatibility with aga-dealcard components
  GetDealsByRatingAndInvestorId(investorId: string, rating: string, period?: string): Promise<RatingDeals[]> {
    return this.GetDealsRatingByInvestorId(investorId, rating, period);
  }
}

export default InvestorService;
